import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import {
  createCompanyDigitalCertificate,
  getCompanyDigitalCertificateById,
  updateCompanyDigitalCertificateById,
} from "../../../../services/companyDigitalCertificate.service";
import {
  FormContainer,
  FormTabs,
  Treturn,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import MainApi from "../../../../services/mainApi.service";
import {
  digitalCertificateSchema,
  TDigitalCertificateForm,
} from "./digitalCertificateSchema";
import { useNavigate, useParams } from "react-router-dom";
import DigitalCertificateFilePage from "./DigitalCertificateFile/DigitalCertificateFilePage";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import {
  ICompanyDigitalCertificateTypeDocument,
  INewCompanyDigitalCertificate,
} from "../../../../models/company-digital-certificate";
import { getCompanyDigitalCertificateDocumentDigits } from "../digitalCertificateDocument";
import { DigitalCertificateFields } from "./DigitalCertificateFields";

const translationPath = "page.register.digital_certificates.";
const GENERIC_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const TYPE_DOCUMENT_PARAMS = {
  Identifier: "CompanyDigitalCertificateTypeDocument",
};
const typeDocumentService = new MainApi<ICompanyDigitalCertificateTypeDocument>(
  GENERIC_ROUTE,
);

const DigitalCertificate = () => {
  const navigate = useNavigate();

  const { id, eI } = useParams();

  const { t } = useTranslation();

  const { setMessage } = useNotificationContext();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createCompanyDigitalCertificate,
  );
  const { sendRequest: update, loading: uLoading } = useFetch(
    updateCompanyDigitalCertificateById,
  );
  const { sendRequest: get, loading: gLoading } = useFetch(
    getCompanyDigitalCertificateById,
  );
  const { sendRequest: listTypeDocuments, loading: tdLoading } = useFetch(
    typeDocumentService.list,
  );

  const buildPayload = useCallback(
    (
      v: TDigitalCertificateForm,
      id?: string,
    ): INewCompanyDigitalCertificate => ({
      ...(id ? { id } : {}),
      name: v.name,
      enable: v.enable,
      useCertillionLocalAgent: v.useCertillionLocalAgent,
      typeDocumentId: v.typeDocument!.id,
      document: getCompanyDigitalCertificateDocumentDigits(v.document),
    }),
    [],
  );

  const saveHandler = (v: TDigitalCertificateForm) =>
    save(buildPayload(v));

  const updateHandler = (v: TDigitalCertificateForm, id: string) =>
    update({
      item: buildPayload(v, id),
      id: id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TDigitalCertificateForm>> => {
      const { data, success } = await get(id);

      if (!success || !data) {
        return {
          data: null,
          success: false,
        };
      }

      let typeDocument: ICompanyDigitalCertificateTypeDocument | null | undefined =
        data.typeDocument;

      if (!typeDocument && data.typeDocumentId) {
        const { data: typeDocuments } = await listTypeDocuments(
          TYPE_DOCUMENT_PARAMS,
        );
        typeDocument =
          typeDocuments?.find((item) => item.id === data.typeDocumentId) ||
          null;
      }

      return {
        data: {
          id: data.id,
          name: data.name,
          enable: data.enable,
          useCertillionLocalAgent: data.useCertillionLocalAgent,
          document: getCompanyDigitalCertificateDocumentDigits(data.document),
          typeDocument: typeDocument ?? null,
        },
        success: true,
      };
    },
    [get, listTypeDocuments],
  );

  const redirectOnSuccess = useCallback(
    (data?: any) => {
      if (!id) {
        if (data) {
          setMessage({
            message: t(translationPath + "created_successfully"),
            type: "success",
          });
          navigate(
            `/e/${eI}/dashboard/admSystem/companyDigitalCertificates/${data.id}/edit`,
            {
              replace: true,
            },
          );
        }
      } else {
        navigate(`/e/${eI}/dashboard/admSystem/companyDigitalCertificates/`);
      }
    },
    [navigate, setMessage, t, id, eI],
  );

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        label: t(translationPath + "digital_certificate_files"),
        icon: <WorkspacePremiumIcon fontSize="small" />,
        component: <DigitalCertificateFilePage />,
      },
    ];

    return baseTabs;
  }, [t]);

  const loading = sLoading || uLoading || gLoading || tdLoading;

  return (
    <>
      <FormContainer<TDigitalCertificateForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "digital_certificate")}
        schema={digitalCertificateSchema}
        subtitle={t(translationPath + "new_digital_certificate")}
        subtitleWatchField="name"
        redirectOnSave={false}
        onSuccess={redirectOnSuccess}
        formActionsOnBottom={false}
        marginBottom={5}
        customHeight="fit-content"
        fields={() => <DigitalCertificateFields />}
      />

      {id && (
        <Grid container spacing={2} sx={{ width: "80%", margin: "0 auto" }}>
          <Grid item sm={12} mb={5}>
            <FormTabs tabs={tabs} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default DigitalCertificate;
